import SecureLS from "secure-ls";

import { isShareView } from "../share/shareView";

const ls = new SecureLS();

// A recipient's viewer (/s/<token>) READS what this browser remembers but never writes or removes anything, the site id included (it used to be the one exception, and a customer who opened a clip in the browser
// they are logged in with came back to a dashboard whose site id was the stand-in's): the snippet's display options and the playback it shows must not overwrite the owner's own saved layout, range or place.
const readOnlyHere = (_key?: string): boolean => isShareView();

// What a recipient changes while they watch (a box moved or removed, a view option) is kept in MEMORY for this visit only: it is read back the way a saved value would be, so the dashboard behaves, and it is gone
// when the page is reloaded (which is what "Reset to defaults" is). Nothing is ever written to their browser.
const visit = new Map<string, unknown>();
const REMOVED = Symbol("removed");

const storage = {
  set: (key: string, data: unknown): void => {
    if (readOnlyHere(key)) { visit.set(key, data); return; }
    ls.set(key, data);
  },

  get: (key: string): string => (isShareView() && visit.has(key) ? (visit.get(key) === REMOVED ? "" : (visit.get(key) as string)) : ls.get(key)),

  // Safe counterpart to JSON.parse(storage.get(key)) - a stored value of
  // undefined (e.g. from JSON.stringify(undefined) getting written by a
  // caller) round-trips as the literal string "undefined", which crashes
  // JSON.parse on every future read instead of just on the write that
  // caused it. Returns null for anything missing or unparsable so a bad
  // value self-heals on the next successful write instead of permanently
  // wedging every load.
  getJSON: <T = any>(key: string): T | null => {
    const raw = isShareView() && visit.has(key) ? (visit.get(key) === REMOVED ? "" : visit.get(key)) : ls.get(key);
    if (!raw) {
      return null;
    }
    // Some callers write already-deserialized objects directly (e.g.
    // Settings.tsx's storage.set("bubbleParameters", {...}), not
    // JSON.stringify'd first) - secure-ls's own set()/get() already handle
    // serialization internally in that case, so ls.get() hands back the
    // real object, not a JSON string. Only parse when it's actually a
    // string; otherwise it's already the value.
    if (typeof raw !== "string") {
      return raw;
    }
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  },

  remove: (key: string): void => {
    if (readOnlyHere(key)) { visit.set(key, REMOVED); return; }
    ls.remove(key);
  },

  removeAll: (): void => {
    if (readOnlyHere()) return;
    ls.removeAll();
  },

  clear: (): void => {
    if (readOnlyHere()) return;
    ls.clear();
  },

  getAllKeys: (): string[] => ls.getAllKeys(),
};

export default storage;
